import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

// Admin Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ProjectDetails from './pages/ProjectDetails';
import Profile from './pages/Profile';
import SetPassword from './pages/SetPassword';
import UserManagement from './pages/UserManagement';
import ClientManagement from './pages/ClientManagement';
import PriceTester from './pages/PriceTester';

// Public Pages
import PublicIndex from './pages/public/Index.tsx';
import PublicProducts from './pages/public/Products.tsx';
import PublicAbout from './pages/public/About.tsx';
import PublicServices from './pages/public/Services.tsx';
import PublicQuality from './pages/public/Quality.tsx';
import PublicDownloads from './pages/public/Downloads.tsx';
import PublicContact from './pages/public/Contact.tsx';
import PublicNotFound from './pages/public/NotFound.tsx';
import ProductPage from './pages/public/ProductPage.tsx';
import DoorCategory from './pages/public/DoorCategory.tsx';
import PublicProjects from './pages/public/Projects.tsx';
import PublicProjectCase from './pages/public/ProjectCase.tsx';
import PublicKnowledge from './pages/public/Knowledge.tsx';
import PublicArticle from './pages/public/ArticlePage.tsx';
import PublicCart from './pages/public/Cart.tsx';
import PublicConfigurator from './pages/public/Configurator.tsx';
import LanguageComingSoon from './pages/public/LanguageComingSoon.tsx';

// Components
import Header from './components/Header';
import PrivateRoute from './components/PrivateRoute';
import ScrollToTop from './components/ScrollToTop';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { LanguageProvider } from './i18n/LanguageContext';
import { ACTIVE_LANGS } from './i18n/config';

const En = ({ children }) => <LanguageProvider value="en">{children}</LanguageProvider>;
const Et = ({ children }) => <LanguageProvider value="et">{children}</LanguageProvider>;

// Gates language routes whose translations are not yet approved for
// publication (currently Danish). In production an unpublished language
// renders a noindexed "coming soon" page; in development the real pages
// render (English fallback where translations are missing).
const LanguageGate = ({ lang, children }) => {
  const allowed =
    ACTIVE_LANGS.includes(lang) || process.env.NODE_ENV === 'development';
  return (
    <LanguageProvider value={lang}>
      {allowed ? children : <LanguageComingSoon />}
    </LanguageProvider>
  );
};
const Da = ({ children }) => <LanguageGate lang="da">{children}</LanguageGate>;
const DoorLeafEditor = React.lazy(() => import('./pages/dev/DoorLeafEditor'));
const DrawingEditor = React.lazy(() => import('./pages/dev/DrawingEditor'));
const StudioWorkspace = React.lazy(() => import('./pages/dev/StudioWorkspace'));

function App() {
  return (
    <AuthProvider>
      <CartProvider>
      <Router>
          <ScrollToTop />
          <div className="App">
            <Routes>
              <Route path="/dev/render-batch" element={
                <PrivateRoute><Navigate to="/dev/door-leaf" replace /></PrivateRoute>
              } />
              <Route path="/dev/door-leaf" element={
                <PrivateRoute><React.Suspense fallback={<p>Loading door editor…</p>}><StudioWorkspace section="editor"><DoorLeafEditor /></StudioWorkspace></React.Suspense></PrivateRoute>
              } />
              <Route path="/dev/drawing-editor" element={
                <PrivateRoute><React.Suspense fallback={<p>Loading drawing editor…</p>}><DrawingEditor /></React.Suspense></PrivateRoute>
              } />
              <Route path="/admin/set-password" element={<SetPassword />} />
              {/* Public Routes (English) */}
              <Route path="/" element={<En><PublicIndex /></En>} />
              <Route path="/products" element={<En><PublicProducts /></En>} />
              <Route path="/about" element={<En><PublicAbout /></En>} />
              <Route path="/services" element={<En><PublicServices /></En>} />
              <Route path="/quality" element={<En><PublicQuality /></En>} />
              <Route path="/downloads" element={<En><PublicDownloads /></En>} />
              <Route path="/contact" element={<En><PublicContact /></En>} />
              <Route path="/exterior-doors" element={<En><DoorCategory contentKey="categories.exteriorDoors" seoKey="seo.exteriorDoors" canonicalPath="/exterior-doors" modelSlugs={['smartline-standard', 'smartline-inswing']} /></En>} />
              <Route path="/storage-doors" element={<En><DoorCategory contentKey="categories.storageDoors" seoKey="seo.storageDoors" canonicalPath="/storage-doors" modelSlugs={['cottage-door', 'meranti-door', 'warm-storage-door-18']} /></En>} />
              <Route path="/outward-opening-exterior-doors" element={<En><DoorCategory contentKey="categories.outwardDoors" seoKey="seo.outwardDoors" canonicalPath="/outward-opening-exterior-doors" modelSlugs={['smartline-standard']} /></En>} />
              <Route path="/inward-opening-exterior-doors" element={<En><DoorCategory contentKey="categories.inwardDoors" seoKey="seo.inwardDoors" canonicalPath="/inward-opening-exterior-doors" modelSlugs={['smartline-inswing']} /></En>} />
              <Route path="/custom-exterior-doors" element={<En><DoorCategory contentKey="categories.customDoors" seoKey="seo.customDoors" canonicalPath="/custom-exterior-doors" modelSlugs={['smartline-standard', 'smartline-inswing', 'cottage-door', 'meranti-door', 'warm-storage-door-18']} /></En>} />
              <Route path="/exterior-doors-for-prefabricated-houses" element={<En><DoorCategory contentKey="categories.prefabDoors" seoKey="seo.prefabDoors" canonicalPath="/exterior-doors-for-prefabricated-houses" modelSlugs={['smartline-standard', 'smartline-inswing']} /></En>} />
              <Route path="/projects" element={<En><PublicProjects /></En>} />
              <Route path="/projects/:slug" element={<En><PublicProjectCase /></En>} />
              <Route path="/knowledge" element={<En><PublicKnowledge /></En>} />
              <Route path="/knowledge/:slug" element={<En><PublicArticle /></En>} />
              <Route path="/products/:slug" element={<En><ProductPage /></En>} />
              <Route path="/cart" element={<En><PublicCart /></En>} />
              <Route path="/configurator" element={<En><PublicConfigurator /></En>} />

              {/* Public Routes (Estonian) */}
              <Route path="/et" element={<Et><PublicIndex /></Et>} />
              <Route path="/et/products" element={<Et><PublicProducts /></Et>} />
              <Route path="/et/about" element={<Et><PublicAbout /></Et>} />
              <Route path="/et/services" element={<Et><PublicServices /></Et>} />
              <Route path="/et/quality" element={<Et><PublicQuality /></Et>} />
              <Route path="/et/downloads" element={<Et><PublicDownloads /></Et>} />
              <Route path="/et/contact" element={<Et><PublicContact /></Et>} />
              <Route path="/et/exterior-doors" element={<Et><DoorCategory contentKey="categories.exteriorDoors" seoKey="seo.exteriorDoors" canonicalPath="/exterior-doors" modelSlugs={['smartline-standard', 'smartline-inswing']} /></Et>} />
              <Route path="/et/storage-doors" element={<Et><DoorCategory contentKey="categories.storageDoors" seoKey="seo.storageDoors" canonicalPath="/storage-doors" modelSlugs={['cottage-door', 'meranti-door', 'warm-storage-door-18']} /></Et>} />
              <Route path="/et/outward-opening-exterior-doors" element={<Et><DoorCategory contentKey="categories.outwardDoors" seoKey="seo.outwardDoors" canonicalPath="/outward-opening-exterior-doors" modelSlugs={['smartline-standard']} /></Et>} />
              <Route path="/et/inward-opening-exterior-doors" element={<Et><DoorCategory contentKey="categories.inwardDoors" seoKey="seo.inwardDoors" canonicalPath="/inward-opening-exterior-doors" modelSlugs={['smartline-inswing']} /></Et>} />
              <Route path="/et/custom-exterior-doors" element={<Et><DoorCategory contentKey="categories.customDoors" seoKey="seo.customDoors" canonicalPath="/custom-exterior-doors" modelSlugs={['smartline-standard', 'smartline-inswing', 'cottage-door', 'meranti-door', 'warm-storage-door-18']} /></Et>} />
              <Route path="/et/exterior-doors-for-prefabricated-houses" element={<Et><DoorCategory contentKey="categories.prefabDoors" seoKey="seo.prefabDoors" canonicalPath="/exterior-doors-for-prefabricated-houses" modelSlugs={['smartline-standard', 'smartline-inswing']} /></Et>} />
              <Route path="/et/projects" element={<Et><PublicProjects /></Et>} />
              <Route path="/et/projects/:slug" element={<Et><PublicProjectCase /></Et>} />
              <Route path="/et/knowledge" element={<Et><PublicKnowledge /></Et>} />
              <Route path="/et/knowledge/:slug" element={<Et><PublicArticle /></Et>} />
              <Route path="/et/products/:slug" element={<Et><ProductPage /></Et>} />
              <Route path="/et/cart" element={<Et><PublicCart /></Et>} />
              <Route path="/et/configurator" element={<Et><PublicConfigurator /></Et>} />
              <Route path="/et/*" element={<Et><PublicNotFound /></Et>} />

              {/* Danish routes — registered but gated. Until 'da' is added to
                  ACTIVE_LANGS (i18n/config.js) these render a noindexed
                  "coming soon" page in production; real pages in dev. */}
              <Route path="/da" element={<Da><PublicIndex /></Da>} />
              <Route path="/da/products" element={<Da><PublicProducts /></Da>} />
              <Route path="/da/about" element={<Da><PublicAbout /></Da>} />
              <Route path="/da/services" element={<Da><PublicServices /></Da>} />
              <Route path="/da/quality" element={<Da><PublicQuality /></Da>} />
              <Route path="/da/downloads" element={<Da><PublicDownloads /></Da>} />
              <Route path="/da/contact" element={<Da><PublicContact /></Da>} />
              <Route path="/da/exterior-doors" element={<Da><DoorCategory contentKey="categories.exteriorDoors" seoKey="seo.exteriorDoors" canonicalPath="/exterior-doors" modelSlugs={['smartline-standard', 'smartline-inswing']} /></Da>} />
              <Route path="/da/storage-doors" element={<Da><DoorCategory contentKey="categories.storageDoors" seoKey="seo.storageDoors" canonicalPath="/storage-doors" modelSlugs={['cottage-door', 'meranti-door', 'warm-storage-door-18']} /></Da>} />
              <Route path="/da/outward-opening-exterior-doors" element={<Da><DoorCategory contentKey="categories.outwardDoors" seoKey="seo.outwardDoors" canonicalPath="/outward-opening-exterior-doors" modelSlugs={['smartline-standard']} /></Da>} />
              <Route path="/da/inward-opening-exterior-doors" element={<Da><DoorCategory contentKey="categories.inwardDoors" seoKey="seo.inwardDoors" canonicalPath="/inward-opening-exterior-doors" modelSlugs={['smartline-inswing']} /></Da>} />
              <Route path="/da/custom-exterior-doors" element={<Da><DoorCategory contentKey="categories.customDoors" seoKey="seo.customDoors" canonicalPath="/custom-exterior-doors" modelSlugs={['smartline-standard', 'smartline-inswing', 'cottage-door', 'meranti-door', 'warm-storage-door-18']} /></Da>} />
              <Route path="/da/exterior-doors-for-prefabricated-houses" element={<Da><DoorCategory contentKey="categories.prefabDoors" seoKey="seo.prefabDoors" canonicalPath="/exterior-doors-for-prefabricated-houses" modelSlugs={['smartline-standard', 'smartline-inswing']} /></Da>} />
              <Route path="/da/projects" element={<Da><PublicProjects /></Da>} />
              <Route path="/da/projects/:slug" element={<Da><PublicProjectCase /></Da>} />
              <Route path="/da/knowledge" element={<Da><PublicKnowledge /></Da>} />
              <Route path="/da/knowledge/:slug" element={<Da><PublicArticle /></Da>} />
              <Route path="/da/products/:slug" element={<Da><ProductPage /></Da>} />
              <Route path="/da/cart" element={<Da><PublicCart /></Da>} />
              <Route path="/da/configurator" element={<Da><PublicConfigurator /></Da>} />
              <Route path="/da/*" element={<Da><PublicNotFound /></Da>} />

              <Route path="*" element={<PublicNotFound />} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
              <Route path="/admin/login" element={
                <div>
                  <ToastContainer position="top-right" autoClose={3000} />
                  <Login />
                </div>
              } />
              <Route path="/admin/dashboard" element={
                <div>
                  <Header />
                  <ToastContainer position="top-right" autoClose={3000} />
                  <main className="container py-4">
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  </main>
                </div>
              } />
              <Route path="/admin/project/:id" element={
                <div>
                  <Header />
                  <ToastContainer position="top-right" autoClose={3000} />
                  <main className="container py-4">
                    <PrivateRoute>
                      <ProjectDetails />
                    </PrivateRoute>
                  </main>
                </div>
              } />
              <Route path="/admin/profile" element={
                <div>
                  <Header />
                  <ToastContainer position="top-right" autoClose={3000} />
                  <main className="container py-4">
                    <PrivateRoute>
                      <Profile />
                    </PrivateRoute>
                  </main>
                </div>
              } />
              <Route path="/admin/users" element={
                <div>
                  <Header />
                  <ToastContainer position="top-right" autoClose={3000} />
                  <main className="container py-4">
                    <PrivateRoute>
                      <UserManagement />
                    </PrivateRoute>
                  </main>
                </div>
              } />
              <Route path="/admin/clients" element={
                <div>
                  <Header />
                  <ToastContainer position="top-right" autoClose={3000} />
                  <main className="container py-4">
                    <PrivateRoute>
                      <ClientManagement />
                    </PrivateRoute>
                  </main>
                </div>
              } />
              <Route path="/admin/pricing" element={
                <div>
                  <Header />
                  <ToastContainer position="top-right" autoClose={3000} />
                  <main className="container py-4">
                    <PrivateRoute>
                      <PriceTester />
                    </PrivateRoute>
                  </main>
                </div>
              } />
            </Routes>
          </div>
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
